/**
 * ONE definition of "earned" for the crew app (Dashboard and Schedule → Done
 * must agree). A completed job is earned — the ledger (server/services/crewPay)
 * pays its estimate the moment it is finished, clock or no clock — and the
 * amount is the estimate (owner's rule), never the clock.
 *
 * The missing-time flag is back (owner, Sep 24 2026): a finished job with no
 * real clock time — never started, or its minutes copied from the estimate —
 * is flagged "no time" everywhere, so the crew see it and start the clock next
 * time. It is still paid; the flag is about the record, not the money.
 */
// A skipped or canceled stop pays nothing (server/services/crewPay computeJobPay).
// The crew's /jobs list still carries a canceled stop so the day shows it, so
// it is zeroed here rather than left at its estimate.
const OFF = ['skipped', 'canceled', 'cancelled'];
export function payOf(job: any): number {
  if (!job || OFF.includes(String(job.status))) return 0;
  // Once the ledger has written the stop, its figure IS the pay (GET /jobs
  // my_pay_ledger: this person's lines on the job — estimate + paid drive, or
  // the flat amount, less nothing). Until then, the estimate the office shows.
  if (job.my_pay_ledger != null && job.my_pay_ledger !== '') return Number(job.my_pay_ledger) || 0;
  return job.crew_pay_is_manual ? Number(job.crew_pay_override || 0) : Number(job.estimated_crew_pay || 0);
}

export function hasMeasuredTime(job: any): boolean {
  return Number(job?.actual_minutes) > 0 && !job?.actual_minutes_is_derived;
}

/** Finished, but the clock never ran — shown with the "no time" flag. */
export function isMissingTime(job: any): boolean {
  return job?.status === 'complete' && !job?.crew_pay_is_manual && !hasMeasuredTime(job);
}

// What the ledger does (server/services/crewPay): a completed stop pays its
// set price the moment it is finished, clock or no clock. "Earned" says the same.
export function isEarned(job: any): boolean {
  return job?.status === 'complete';
}

export function splitEarned(jobs: any[]) {
  const complete = (jobs || []).filter(j => j.status === 'complete');
  const earnedJobs = complete.filter(isEarned);
  // Not "not counted" any more — still paid — but the ones missing their clock time.
  const notCounted = complete.filter(isMissingTime);
  const sum = (xs: any[]) => xs.reduce((s, j) => s + payOf(j), 0);
  return {
    earnedJobs, notCounted,
    earned: sum(earnedJobs),
    notCountedPay: sum(notCounted),
    pending: sum((jobs || []).filter(j => j.status !== 'complete' && !OFF.includes(String(j.status)))),
  };
}

export function notCountedWarning(notCounted: any[], notCountedPay: number): string | null {
  const n = (notCounted || []).length;
  if (!n) return null;
  return `⚠ ${n} finished job${n === 1 ? '' : 's'} with no clock time — still paid on the estimate ($${(notCountedPay || 0).toFixed(2)}). Tap Start when you arrive so your time counts.`;
}
